import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
} from '@/infra/api-client'
import { EMPTY, type Observable } from 'rxjs'
import { delay, filter, map, retry, share, switchMap } from 'rxjs/operators'
import { connectToSSE } from '@/utils/sse'
import { isOnline$ } from '@/utils/browser-util'
import { filterNonNil } from '@/utils/rx-util'
import { toURLSearchParams } from '@/utils/url-util'
import {
  type MessageBankResource,
  type MessageBankReplyResource,
} from '@/features/messaging/api/resources/message-bank'
import {
  type ConversationResource,
  type ConversationUnreadCountResponseResource,
  type MessageResource,
  type MessageEventResource,
  type ListMessagesRequest,
  type UpdateParticipantRequest,
  type OnlineStatusResource,
  type ParticipantResource,
} from '@/features/messaging/api/resources/messages'
import type {
  RoomConnectionDescriptorResource,
  RoomResource,
} from '@/features/messaging/api/resources/rooms'

/**
 * Messaging API root.
 */
export class MessagingApi {
  readonly conversations
  readonly messages
  readonly participants
  readonly users
  readonly messageBanks
  readonly rooms

  /**
   * Realtime messaging events.
   */
  readonly events$: Observable<MessageEventResource>

  constructor(opts: ApiClientOptions) {
    this.conversations = new ConversationApi(opts)
    this.messages = new MessagesApi(opts)
    this.participants = new ParticipantApi(opts)
    this.users = new UserStatusApi(opts)
    this.messageBanks = new MessageBankApi(opts)
    this.rooms = new RoomsApi(opts)

    this.events$ = opts.token$.pipe(
      switchMap((token) =>
        token
          ? connectToSSE<unknown>(`${opts.baseURL!}/?__authorization=${token}`)
          : EMPTY,
      ),
      map((message) => {
        try {
          if (typeof message.data !== 'string') {
            console.error('Invalid event data', message)
            return null
          }

          return JSON.parse(message.data) as MessageEventResource
        } catch (err) {
          console.error('Failed to parse event data', err)
          return null
        }
      }),
      filterNonNil(),
      retry({
        // Only retry while we're online.
        delay: () => isOnline$.pipe(filter(Boolean), delay(1_000)),
        resetOnSuccess: true,
      }),
      share(),
    )
  }
}

/**
 * API for conversations.
 */
export class ConversationApi extends ApiClient {
  /**
   * Gets a conversation by ID.
   */
  get(id: string): Promise<ConversationResource> {
    return this._get(`v3/conversations/${id}`)
  }

  /**
   * Gets the human-sent messages of a conversation in a workspace.
   */
  getHumanMessages(
    id: string,
    workspaceId: string,
  ): Promise<MessageResource[]> {
    return this._get(`v3/conversations/${id}/${workspaceId}/humanMessages`)
  }

  /**
   * Gets unread message counts for all conversations.
   */
  getUnreadCounts(
    signal?: AbortSignal,
  ): Promise<ConversationUnreadCountResponseResource> {
    return this._get('v3/messages/unread', undefined, { signal })
  }
}

/**
 * API for messages.
 */
export class MessagesApi extends ApiClient {
  /**
   * Lists messages for a conversation.
   */
  list(
    conversationId: string,
    request: ListMessagesRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<MessageResource>> {
    return this._get(
      `v3/conversations/${conversationId}/messages`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  /**
   * Sends a text message to a conversation.
   */
  sendText(conversationId: string, text: string): Promise<MessageResource> {
    return this._post(`v3/conversations/${conversationId}/messages/text`, {
      text,
      source: 'WORK_PORTAL',
    })
  }

  /**
   * Sends a note to a conversation.
   */
  sendNote(conversationId: string, text: string): Promise<MessageResource> {
    return this._post(`v3/conversations/${conversationId}/messages/note`, {
      text,
      source: 'WORK_PORTAL',
    })
  }

  /**
   * Sends a note that only admins can see to a conversation.
   */
  sendAdminNote(
    conversationId: string,
    text: string,
  ): Promise<MessageResource> {
    return this._post(`v3/conversations/${conversationId}/messages/adminNote`, {
      text,
      source: 'WORK_PORTAL',
    })
  }

  /**
   * Sends a note that only the team can see to a conversation.
   */
  sendTeamNote(
    conversationId: string,
    teamId: string,
    text: string,
  ): Promise<MessageResource> {
    return this._post(`v3/conversations/${conversationId}/messages/teamNote`, {
      text,
      team_id: teamId,
      source: 'WORK_PORTAL',
    })
  }

  /**
   * Sends a message as an admin to a conversation.
   */
  sendAdmin(conversationId: string, text: string): Promise<MessageResource> {
    return this._post(`v3/conversations/${conversationId}/messages/admin`, {
      text,
      source: 'WORK_PORTAL',
    })
  }

  /**
   * Edits the text of a message.
   */
  update(messageId: string, text: string): Promise<MessageResource> {
    return this._patch(`v3/messages/${messageId}`, {
      text,
    })
  }

  /**
   * Marks messages as read.
   */
  markRead(workspaceId: string, messageIds: string[]): Promise<void> {
    return this._post<unknown, unknown>('v3/messages/read', {
      workspace_id: workspaceId,
      message_ids: messageIds,
    }).then(() => undefined)
  }
}

/**
 * API for participants in conversations.
 */
export class ParticipantApi extends ApiClient {
  /**
   * Updates a participant.
   */
  update(request: UpdateParticipantRequest): Promise<ParticipantResource> {
    return this._patch(
      `v3/conversations/${request.conversation_id}/participants/${request.user_id}/update`,
      request,
    )
  }
}

/**
 * API for user status information.
 */
export class UserStatusApi extends ApiClient {
  /**
   * Checks if users are online.
   */
  isOnline(userPublicIds: string[]): Promise<OnlineStatusResource[]> {
    return this._post(`v3/users/status/id-query`, userPublicIds)
  }
}

/**
 * API for message banks.
 */
export class MessageBankApi extends ApiClient {
  /**
   * Gets the message bank of a workspace.
   */
  getForWorkspace(
    workspaceId: string,
    signal?: AbortSignal,
  ): Promise<MessageBankResource> {
    return this._get(
      `v3/message-banks/find`,
      toURLSearchParams({ workspace_id: workspaceId }),
      { signal },
    )
  }

  /**
   * Lists the replies of a message bank, by title.
   */
  listReplies(
    messageBankId: string,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<MessageBankReplyResource>> {
    return this._get(
      `v3/message-bank-replies`,
      toURLSearchParams({
        message_bank_id: messageBankId,
        order_by: 'title asc',
      }),
      { signal },
    )
  }
}

/**
 * API for rooms.
 */
export class RoomsApi extends ApiClient {
  /**
   * Gets a room by ID.
   */
  get(id: string): Promise<RoomResource> {
    return this._get(`v3/rooms/${id}`)
  }

  /**
   * Gets the connection info to join a room.
   */
  join(id: string): Promise<RoomConnectionDescriptorResource> {
    return this._get(`v3/rooms/${id}/join`)
  }
}
